import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getSendStatuses,
  getSentEmail,
  getSentEmailNotificationTypes,
  getSentEmailOperationCodes,
  getSentEmails,
  resetSentEmailRetry,
  type SentEmailQuery,
} from '../api/sentEmailApi'
import type { PagedResponse, SentEmailListItem } from '../types'

const KEY = 'admin-sent-emails'

/**
 * Admin → Bildirimler sunucu-durumu (078): filtreli/sayfalı liste + bildirim tipi, durum ve işlem
 * kodu seçenekleri + tek yazma eylemi (`resetRetry`, 078-b).
 *
 * <p><b>Tek mutation vardır</b>: "deneme sayısını sıfırla". Ekran hâlâ CRUD içermez — kayıtları
 * uygulama üretir, gönderim durumunu dış geçit ilerletir.
 *
 * <p><b>`i18n.language` queryKey'in parçasıdır:</b> hem satırlardaki `subject`/`sendStatusName` hem de
 * filtre seçeneklerinin adları sunucuda `Accept-Language`'e göre çözülür. Dil TR↔EN değiştiğinde
 * anahtar değişmezse önbellekten eski dildeki yanıt sunulur ve konu/durum eski dilde kalır (kaynak
 * §9.13 tuzağı).
 *
 * <p><b>`keepPreviousData`:</b> sunucu-taraflı sayfalamada sayfa değişirken `isLoading` yeniden `true`
 * olur; paylaşılan `DataTable`'ın iskelet dalı paginator'ı render etmediği için sayfalama kontrolü
 * kaybolup tablo titrer. Önceki veri korunarak bu önlenir (repodaki diğer sunucu-sayfalı listelerle
 * aynı kalıp).
 */
export function useSentEmails(query: SentEmailQuery) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: [
      KEY,
      i18n.language,
      query.q ?? '',
      query.notificationTypeId ?? null,
      query.sendStatusId ?? null,
      query.operationCodeId ?? null,
      query.minRetryCount ?? null,
      query.plannedStart ?? null,
      query.plannedEnd ?? null,
      query.sendingStart ?? null,
      query.sendingEnd ?? null,
      query.page,
      query.size,
    ],
    queryFn: () => getSentEmails(query),
    placeholderData: keepPreviousData,
  })

  // Bildirim tipi kataloğu — durum/işlem kodu combolarıyla aynı desen: filtrelerden bağımsız,
  // kendi anahtarında, dile bağlı.
  const notificationTypes = useQuery({
    queryKey: [KEY, 'notification-types', i18n.language],
    queryFn: getSentEmailNotificationTypes,
  })

  const sendStatuses = useQuery({
    queryKey: [KEY, 'send-statuses', i18n.language],
    queryFn: getSendStatuses,
  })

  // Katalog niteliğinde ve filtrelerden bağımsızdır → kendi anahtarında durur; liste her filtre
  // değişiminde yeniden çekilirken bu sorgu önbellekten servis edilir.
  const operationCodes = useQuery({
    queryKey: [KEY, 'operation-codes', i18n.language],
    queryFn: getSentEmailOperationCodes,
  })

  /**
   * Güncellenmiş satırı, önbellekteki **tüm** liste sayfalarına hemen yazar (059
   * `useNotificationTemplates.applyUpdated` deseni). Anahtar prefix'le taranır (`setQueriesData`):
   * liste anahtarı dil + yedi filtre + sayfa + boyut taşır, çağıranın o bileşimi yeniden kurması
   * kırılgan olurdu.
   *
   * <p><b>Prefix'e `i18n.language` DAHİL edilir</b>: satırdaki `sendStatusName`/`notificationTypeName`
   * sunucuda `Accept-Language`'e göre çözülür, yani kayıt dile özgüdür. Yalnız `[KEY]` ile taransaydı
   * aktif dilin satırı diğer dilin önbelleğine yazılır ve dil değiştirildiğinde tablo kısa süre yanlış
   * dilde durum adı gösterirdi. Dil segmenti katalog sorgularını da doğal olarak dışarıda bırakır —
   * onların anahtarında 1. eleman sabit bir etikettir (`'send-statuses'` vb.), dil değil.
   */
  const applyUpdated = (updated: SentEmailListItem) =>
    qc.setQueriesData<PagedResponse<SentEmailListItem>>(
      { queryKey: [KEY, i18n.language] },
      (prev) =>
        prev && Array.isArray(prev.content)
          ? { ...prev, content: prev.content.map((r) => (r.id === updated.id ? updated : r)) }
          : prev,
    )

  /**
   * Deneme sayısını sıfırla → kaydı yeniden kuyruğa al (078-b).
   *
   * <p><b>`invalidateQueries` promise'i bilinçli olarak DÖNDÜRÜLMEZ</b> (gövde `{}` içinde): React
   * Query `onSuccess`'ten dönen promise'i bekler ve mutation ancak o zaman "settled" olur. Beklenirse
   * bu anahtar altındaki tüm sorguların (liste + üç katalog) yeniden çekilmesi bitene kadar mutation
   * açık kalır; biri yavaşlar ya da hiç dönmezse `onSettled` çalışmaz → satırın `busyId` kilidi
   * açılmaz ve buton kalıcı olarak disabled kalır (059'da belgelenen tuzak). Satır zaten
   * `applyUpdated` ile güncellendiğinden tazeleme arka planda sürebilir.
   */
  const resetRetry = useMutation({
    mutationFn: (id: number) => resetSentEmailRetry(id),
    onSuccess: (data) => {
      applyUpdated(data)
      qc.invalidateQueries({ queryKey: [KEY] })
    },
  })

  return { list, notificationTypes, sendStatuses, operationCodes, resetRetry }
}

/**
 * Tek kaydın detayı — **yalnız modal açıkken** çekilir (`enabled`), böylece liste gezinirken gereksiz
 * istek gitmez ve modal kapanınca bayat veri gösterilmez.
 */
export function useSentEmailDetail(id: number | null) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: [KEY, 'detail', id, i18n.language],
    queryFn: () => getSentEmail(id as number),
    enabled: id != null,
  })
}
